import { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { OpenBankingModalState, useLazyOpenBankingConsentUrl, useOpenBankingConsentState } from 'src/hooks/openBanking';
import { IncomeVerificationType, requiredFieldError } from '@brighte/validation';
import {
  OpenBankingExplanationModal,
  OpenBankingFeedbackModal,
  OpenBankingInstructionModal,
} from '@components/OpenBanking';
import { SelectionCardTextBanner } from '@components/SelectionCard';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import { OpenBankingFeedBack, useAddOpenBankingFeedBackMutation } from '@graphql/index';
import { useOpenBankingSteps } from '@provider';
import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { ExclamationIcon } from '@spark-web/icon';
import { Text } from '@spark-web/text';
import { EOpenBankingError, INCOME_VERIFICATION, isJointApplicant } from '@utils';
import { useOpenBankingConnectedBanks } from '../hooks';
import OpenBankingConnectedAccounts from './OpenBankingConnectedAccounts';
import OpenBankingIncome from './OpenBankingIncome';
import SelfDeclaredIncome from './SelfDeclaredIncome';

interface OpenBankingVerificationProps {
  formContext: UseFormReturn<IncomeVerificationType>;
  trackingId: string;
  isCdrChallenger: boolean;
  isApplicantIncomeTypeSelected: boolean;
  isOpenBankingOptionEnabled: boolean;
  incomeVerification?: IncomeVerificationType;
  setDisabledNavigation: React.Dispatch<React.SetStateAction<boolean>>;
  onShowConnectedAccountsChange?: (value: boolean) => void;
  onSelfDeclaredOverride?: () => void;
  useOccupationGate?: boolean;
}

export function OpenBankingVerification({
  formContext,
  trackingId,
  isCdrChallenger,
  isOpenBankingOptionEnabled,
  incomeVerification,
  setDisabledNavigation,
  isApplicantIncomeTypeSelected,
  onShowConnectedAccountsChange,
  onSelfDeclaredOverride,
  useOccupationGate,
}: OpenBankingVerificationProps) {
  const {
    VERIFY_YOUR_INCOME,
    VERIFY_YOUR_INCOME_DESCRIPTION,
    VERIFY_YOUR_INCOME_DESCRIPTION_CHALLENGER,
    VERIFY_YOUR_INCOME_MISSING_ERROR_MESSAGE,
    AUTOFILL_WITH_CDR_ERROR_BANNER_MESSAGE,
  } = INCOME_VERIFICATION;

  const { setValue, formState, setError, clearErrors } = formContext;

  const usingOpenBanking = useWatch({
    control: formContext.control,
    name: 'applicant.usingOpenBanking',
  });
  const {
    openBankingModalState,
    closeOpenBankingModal,
    setOpenBankingModalState,
    openBankingLocalState,
    setOpenBankingLocalState,
  } = useOpenBankingConsentState();

  const {
    setIsRequestingManualIncomeEdit,
    openBankingIncomeSyncState,
    setOpenBankingIncomeSyncState,
    setOpenBankingConsentComplete,
    isRequestingManualIncomeEdit,
  } = useOpenBankingSteps();

  const savedOpenBankingPreference = incomeVerification?.applicant?.usingOpenBanking;

  const { getOpenBankingConnectedBanks, isFetchingConnectedBanks, uniqueConnectedBanks } = useOpenBankingConnectedBanks(
    {
      enabled: isOpenBankingOptionEnabled,
      onSuccess: data => {
        const connectedBanks = Boolean(data?.openBankingConsents?.length);

        if (connectedBanks && savedOpenBankingPreference === false && usingOpenBanking !== true) {
          setIsRequestingManualIncomeEdit(true);
        } else if (!isRequestingManualIncomeEdit && usingOpenBanking !== false) {
          setValue('applicant.usingOpenBanking', true);
        }
      },
      setDisabledNavigation,
    },
  );

  const openBankingConnected = savedOpenBankingPreference || uniqueConnectedBanks?.length;
  const isOpenBankingIncomeSyncError = openBankingIncomeSyncState.status === 'error';

  const handleConsentRequestSuccess = () => {
    datadogRum.addAction('User completed CDR consent successfully');
    clearErrors('applicant.income');
    setValue('applicant.usingOpenBanking', true);

    getOpenBankingConnectedBanks();
    setOpenBankingLocalState('success');
    setOpenBankingConsentComplete(true);
    setOpenBankingIncomeSyncState({ status: 'loading' });
    closeOpenBankingModal();
  };

  const handleConsentRequestError = (errorType?: EOpenBankingError) => {
    closeOpenBankingModal();

    if (errorType === EOpenBankingError.CONSENT_ENDUSER_DENIED) {
      setOpenBankingModalState(OpenBankingModalState.FEEDBACK);
    } else {
      setOpenBankingIncomeSyncState({ status: 'error', errorType });
      if (openBankingConnected) {
        setValue('applicant.usingOpenBanking', true);
        clearErrors('applicant.usingOpenBanking');
        clearErrors('applicant.income');
        setOpenBankingConsentComplete(true);
        return;
      }
      setValue('applicant.usingOpenBanking', undefined);
      setError('applicant.usingOpenBanking', { message: requiredFieldError() });
    }
  };

  const { getOpenBankingConsentUrl, isFetchingOpenBankingConsentDetails } = useLazyOpenBankingConsentUrl({
    applicationId: trackingId,
    onConsentStart: () => setOpenBankingModalState(OpenBankingModalState.CONSENT_IFRAME),
    onConsentSuccess: handleConsentRequestSuccess,
    onConsentError: handleConsentRequestError,
  });

  const { mutate: addOpenBankingFeedBackMutation } = useAddOpenBankingFeedBackMutation();

  const handleConnectOpenBanking = () => {
    if (openBankingConnected) {
      datadogRum.addAction('User selected CDR consent again to connect to another bank account');
    } else {
      datadogRum.addAction('User selected CDR consent');
    }
    getOpenBankingConsentUrl();

    // DEV_MODE: Simulate successful user consent for development purposes
    if (import.meta.env.DEV && process.env.DEV_MODE !== 'staging') {
      setTimeout(() => {
        handleConsentRequestSuccess();
      }, 500);
    }
  };

  const handleSubmitFeedback = (feedback: Array<OpenBankingFeedBack>) => {
    addOpenBankingFeedBackMutation({ feedback });
    closeOpenBankingModal();
  };

  const handleOpenInstructionModal = () => {
    setOpenBankingModalState(OpenBankingModalState.INSTRUCTION);
  };

  const handleUseManualIncome = () => {
    if (onSelfDeclaredOverride) {
      onSelfDeclaredOverride();
      return;
    }
    setValue('applicant.usingOpenBanking', false);
  };

  // Sync with applicant field
  useEffect(() => {
    if (isOpenBankingIncomeSyncError) {
      if (openBankingConnected) {
        setValue('applicant.usingOpenBanking', true);
        setOpenBankingConsentComplete(true);
        return;
      }
      setValue('applicant.usingOpenBanking', undefined);
      setOpenBankingConsentComplete(false);
      return;
    }
    if (isRequestingManualIncomeEdit) {
      setValue('applicant.usingOpenBanking', false);
      return;
    }
    if (usingOpenBanking === undefined && savedOpenBankingPreference !== undefined) {
      setValue('applicant.usingOpenBanking', savedOpenBankingPreference);
    }
  }, [
    isRequestingManualIncomeEdit,
    isOpenBankingOptionEnabled,
    savedOpenBankingPreference,
    isOpenBankingIncomeSyncError,
    openBankingConnected,
    usingOpenBanking,
  ]);

  const isOpenBankingConsentSuccess =
    openBankingLocalState === 'success' && !isOpenBankingIncomeSyncError && !isRequestingManualIncomeEdit;

  const showConnectedAccounts = openBankingConnected || isOpenBankingConsentSuccess;

  useEffect(() => {
    if (!onShowConnectedAccountsChange) return;
    onShowConnectedAccountsChange(Boolean(showConnectedAccounts));
    return () => onShowConnectedAccountsChange(false);
  }, [showConnectedAccounts, onShowConnectedAccountsChange]);

  useEffect(() => {
    if (showConnectedAccounts) {
      if (usingOpenBanking !== true) {
        setValue('applicant.usingOpenBanking', true);
      }
      clearErrors('applicant.income');
    }
  }, [clearErrors, setValue, showConnectedAccounts, usingOpenBanking]);

  // Don't render if CDR is disabled or no income type selected
  if (!isOpenBankingOptionEnabled || !isApplicantIncomeTypeSelected || isRequestingManualIncomeEdit) {
    return null;
  }

  const isIncomeError = !!formState.errors?.applicant?.income;

  return (
    <>
      <Field
        label={VERIFY_YOUR_INCOME}
        description={
          showConnectedAccounts
            ? ''
            : isCdrChallenger
              ? VERIFY_YOUR_INCOME_DESCRIPTION_CHALLENGER
              : VERIFY_YOUR_INCOME_DESCRIPTION
        }
        tone={isIncomeError ? 'critical' : undefined}
        message={
          usingOpenBanking !== false && isIncomeError && !showConnectedAccounts
            ? VERIFY_YOUR_INCOME_MISSING_ERROR_MESSAGE
            : undefined
        }
      >
        {isOpenBankingIncomeSyncError ? (
          <SelectionCardTextBanner background="secondaryLow">
            <ExclamationIcon tone="secondary" />
            <Text>{AUTOFILL_WITH_CDR_ERROR_BANNER_MESSAGE}</Text>
          </SelectionCardTextBanner>
        ) : null}

        <Box
          paddingY="small"
          display="flex"
          flexDirection="column"
          gap="large"
          className={css({
            borderRadius: '12px',
          })}
        >
          {showConnectedAccounts ? (
            <OpenBankingConnectedAccounts
              onConnect={handleOpenInstructionModal}
              isLoadingConnect={isFetchingOpenBankingConsentDetails}
              uniqueConnectedBanks={uniqueConnectedBanks}
              isFetchingConnectedBanks={isFetchingConnectedBanks}
            />
          ) : (
            <Columns gap="large" collapseBelow="tablet">
              <OpenBankingIncome
                onOpenExplanation={() => setOpenBankingModalState(OpenBankingModalState.EXPLANATION)}
                onConnect={handleOpenInstructionModal}
                isCdrChallenger={isCdrChallenger}
              />
              <SelfDeclaredIncome onUseManualIncome={handleUseManualIncome} useOccupationGate={useOccupationGate} />
            </Columns>
          )}
        </Box>
      </Field>

      <OpenBankingExplanationModal
        isOpen={openBankingModalState === OpenBankingModalState.EXPLANATION}
        onToggle={closeOpenBankingModal}
        onConnect={handleConnectOpenBanking}
        isCdrChallenger={isCdrChallenger}
      />
      <OpenBankingFeedbackModal
        isOpen={openBankingModalState === OpenBankingModalState.FEEDBACK}
        onSubmit={handleSubmitFeedback}
        onToggle={closeOpenBankingModal}
      />
      <OpenBankingInstructionModal
        isOpen={openBankingModalState === OpenBankingModalState.INSTRUCTION}
        isLoading={isFetchingOpenBankingConsentDetails}
        onToggle={closeOpenBankingModal}
        onConnect={handleConnectOpenBanking}
        isCdrChallenger={isCdrChallenger}
      />
    </>
  );
}

export default OpenBankingVerification;
